import { useMemo } from 'react';
import type { MonthlyStatement } from '../hooks/useAccountingData';
import dayjs from 'dayjs';

interface Props {
  data: MonthlyStatement[];
  loading: boolean;
}

function formatAmount(v: number): string {
  if (v >= 10000) return `¥${(v / 10000).toFixed(1)}万`;
  return `¥${v.toLocaleString('zh-CN')}`;
}

export default function MonthlyStatementTable({ data, loading }: Props) {
  const allMonths = useMemo(() => data.slice(0, 12), [data]); // show latest 12
  
  const currentMonthKey = useMemo(() => {
    return dayjs().format('YYYY-MM');
  }, []);
  
  const totals = useMemo(() => {
    return {
      income: allMonths.reduce((s, r) => s + r.income, 0),
      expense: allMonths.reduce((s, r) => s + r.expense, 0),
      profit: allMonths.reduce((s, r) => s + r.profit, 0),
      paidAmount: allMonths.reduce((s, r) => s + r.paidAmount, 0),
      pendingAmount: allMonths.reduce((s, r) => s + r.pendingAmount, 0),
      orders: allMonths.reduce((s, r) => s + r.orders, 0)
    };
  }, [allMonths]);

  const colCount = 7;

  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">月度盈亏报表</h3>
          <p className="text-xs text-gray-400 mt-0.5">近12个月收支核算明细</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50/70">
              <th className="text-left text-xs font-medium text-gray-500 px-5 py-3">月份</th>
              <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">收入</th>
              <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">支出</th>
              <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">利润</th>
              <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">已收款</th>
              <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">待收款</th>
              <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">订单数</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-t border-gray-50 animate-pulse">
                  {Array.from({ length: colCount }).map((_, j) => (
                    <td key={j} className="px-5 py-3">
                      <div className="h-4 bg-gray-100 rounded w-full" />
                    </td>
                  ))}
                </tr>
              ))
            ) : allMonths.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="text-center text-sm text-gray-400 py-12">暂无数据</td>
              </tr>
            ) : (
              allMonths.map((row) => {
                const isCurrentMonth = row.key === currentMonthKey;
                return (
                  <tr key={row.key} className={`border-t border-gray-50 hover:bg-gray-50/50 transition-colors ${isCurrentMonth ? 'bg-teal-50/30' : ''}`}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-800">{row.month}</span>
                        {isCurrentMonth && (
                          <span className="text-xs px-1.5 py-0.5 bg-teal-100 text-teal-600 rounded-md">本月</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <span className="text-sm font-medium text-emerald-600">{row.income > 0 ? formatAmount(row.income) : '-'}</span>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <span className="text-sm text-rose-500">{row.expense > 0 ? formatAmount(row.expense) : '-'}</span>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <span className={`text-sm font-semibold ${row.profit >= 0 ? 'text-teal-600' : 'text-rose-500'}`}>
                        {row.profit === 0 ? '-' : (row.profit > 0 ? '' : '-') + formatAmount(Math.abs(row.profit))}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <span className="text-sm text-gray-600">{row.paidAmount > 0 ? formatAmount(row.paidAmount) : '-'}</span>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      {row.pendingAmount > 0 ? (
                        <span className="text-sm text-amber-600 font-medium">{formatAmount(row.pendingAmount)}</span>
                      ) : (
                        <span className="text-sm text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <span className="text-sm text-gray-600">{row.orders > 0 ? `${row.orders} 单` : '-'}</span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {!loading && allMonths.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-gray-100 bg-gray-50/70">
                <td className="px-5 py-3 text-xs font-semibold text-gray-600">合计</td>
                <td className="px-4 py-3 text-right text-sm font-bold text-emerald-600">
                  {formatAmount(totals.income)}
                </td>
                <td className="px-4 py-3 text-right text-sm font-bold text-rose-500">
                  {formatAmount(totals.expense)}
                </td>
                <td className="px-4 py-3 text-right text-sm font-bold text-teal-600">
                  {formatAmount(totals.profit)}
                </td>
                <td className="px-4 py-3 text-right text-sm font-bold text-gray-700">
                  {formatAmount(totals.paidAmount)}
                </td>
                <td className="px-4 py-3 text-right text-sm font-bold text-amber-600">
                  {formatAmount(totals.pendingAmount)}
                </td>
                <td className="px-4 py-3 text-right text-sm font-bold text-gray-700">
                  {totals.orders} 单
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
